"use client";

/**
 * Suggested fixes for the current search, shown INSIDE the search bar's
 * panel (owner, 2026-10-01: "in the search bar options, with some other
 * icon, a different colour, indicating that it was suggested") - so the
 * listings page and the alert form, which share the bar, both get them.
 * When the search stops making sense: a rent-only tag on a sale search, a price on the wrong scale, a
 * filter that removes nothing, a near-empty page (with the removal that
 * brings the most back), or on a healthy search what people who set the
 * same thing went on to add. The backend decides (services/filter_advice);
 * this renders its ids with the locale's copy and applies the fixes
 * through the page's own mergeParams.
 */
import { useEffect, useMemo, useState } from "react";
import {
  fetchListingsAdvice,
  getFeatureTags,
  type FeatureTagDef,
  type ListingAdviceAction,
  type ListingAdviceItem,
} from "@/lib/api";
import { apiParamsFromSearch } from "@/lib/listing-search-params";
import { trackEvent } from "@/lib/tracking";
import { useLocale, useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import type { ReadonlyURLSearchParams } from "next/navigation";

type Updates = Record<string, string | string[] | null>;

const GROUP_KEYS: Record<string, TranslationKey> = {
  price: "listings.filter.price.short",
  rooms: "listings.filter.rooms.short",
  area: "listings.filter.area",
  location: "advice.g.location",
  listing_kind: "listings.filter.category",
  floor: "listings.filter.floor",
  total_floors: "advice.g.total_floors",
  metro_walk: "listings.filter.metro_walk",
  has_repair: "listings.filter.repair",
  has_mortgage: "listings.filter.has_mortgage",
  has_bill_of_sale: "listings.filter.has_bill_of_sale",
  deal_tier: "listings.filter.deal_tier",
  photo_condition: "listings.filter.photo_tier",
  photo_has: "listings.filter.photo_has",
  seller_type: "listings.filter.seller_type",
  ppm: "advice.g.ppm",
  min_yield: "advice.g.min_yield",
  q: "advice.g.q",
};

const DISMISS_KEY = "menzil.listings.advice.dismissed";

function readDismissed(): Set<string> {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

const fmtNum = (n: number | string) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

export type AdviceRow = {
  key: string;
  text: string;
  /** A tip (people also add ...) rather than a fix. */
  tip: boolean;
  actions: { key: string; label: string; apply: Updates; onPick: () => void }[];
};

export function useListingAdvice(
  searchParams: ReadonlyURLSearchParams | URLSearchParams,
): AdviceRow[] {
  const t = useT();
  const { locale } = useLocale();
  const [items, setItems] = useState<ListingAdviceItem[]>([]);
  const [tags, setTags] = useState<FeatureTagDef[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());

  const params = useMemo(
    () => apiParamsFromSearch(searchParams as ReadonlyURLSearchParams),
    [searchParams],
  );
  const key = useMemo(() => JSON.stringify(params), [params]);

  useEffect(() => {
    setDismissed(readDismissed());
    getFeatureTags().then((v) => setTags(v.tags)).catch(() => {});
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    // Debounced: a burst of chip clicks asks once, after the last one.
    const id = window.setTimeout(() => {
      fetchListingsAdvice(params, { signal: ctrl.signal })
        .then((d) => setItems(d.items || []))
        .catch(() => { /* advice is optional */ });
    }, 350);
    return () => { window.clearTimeout(id); ctrl.abort(); };
    // `key` carries params' identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const sig = (it: ListingAdviceItem) => `${it.id}:${JSON.stringify(it.vars)}`;
  const visible = items.filter((it) => !dismissed.has(sig(it)));

  // One "shown" event per suggestion per search, so the knowledge base
  // can learn which ones people act on.
  const shownKey = visible.map(sig).join("|");
  useEffect(() => {
    visible.forEach((it) => trackEvent("listings.advice", { id: it.id, action: "shown", vars: it.vars }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownKey]);

  const tagLabel = (id: string) => {
    const d = tags.find((x) => x.id === id);
    return d ? d.labels[locale as "az" | "ru" | "en"] || d.labels.az : id;
  };
  const groupLabel = (g: string) =>
    g.startsWith("tag:") ? tagLabel(g.slice(4)) : GROUP_KEYS[g] ? t(GROUP_KEYS[g]) : g;
  // "rooms=3" -> "3 otaqlı"; null when the pair has no readable name.
  const pairLabel = (k: string, v: string): string | null => {
    if (k === "rooms") return t("result.hero.rooms").replace("{n}", v);
    if (k === "listing_kind") {
      if (v === "new") return t("listings.filter.building_type.new");
      if (v === "old") return t("listings.filter.building_type.old");
      if (v === "house") return t("listings.filter.category.house");
    }
    if (k === "leased" && v === "true") return t("listings.filter.transaction.rent");
    if (k === "tags") return tagLabel(v);
    if (k === "metro_walk") return `${t("listings.filter.metro_walk")} ${t("listings.filter.metro_walk.min").replace("{n}", v)}`;
    if (k === "has_repair" && v === "yes") return t("listings.filter.repair.yes");
    if (k === "has_mortgage" && v === "yes") return t("listings.filter.has_mortgage");
    if (k === "has_bill_of_sale" && v === "yes") return t("listings.filter.has_bill_of_sale");
    return null;
  };

  const fill = (s: string, vars: Record<string, string>) =>
    Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(v), s);

  function sentence(it: ListingAdviceItem): string | null {
    const v = it.vars;
    switch (it.id) {
      case "rent_tag_on_sale":
      case "sale_tag_on_rent":
        return fill(t(`advice.${it.id}` as TranslationKey), { tag: tagLabel(String(v.tag)) });
      case "rent_price_scale":
      case "sale_price_scale":
        return fill(t(`advice.${it.id}` as TranslationKey), { n: fmtNum(v.n as number) });
      case "house_floor":
        return t("advice.house_floor");
      case "too_few":
        return Number(v.n) === 0 ? t("advice.too_few_zero") : fill(t("advice.too_few"), { n: String(v.n) });
      case "no_effect":
        return fill(t("advice.no_effect"), { group: groupLabel(String(v.group)) });
      case "people_also": {
        const [ak, av] = String(v.anchor).split("=");
        const anchor = pairLabel(ak, av);
        const step = pairLabel(String(v.key), String(v.value));
        if (!anchor || !step) return null;
        return fill(t("advice.people_also"), { anchor, step, share: String(v.share) });
      }
    }
    return null;
  }

  function actionLabel(a: ListingAdviceAction): string | null {
    const v = a.vars || {};
    switch (a.id) {
      case "switch_to_rent": return t("advice.a.switch_to_rent");
      case "switch_to_sale": return t("advice.a.switch_to_sale");
      case "drop_tag": return fill(t("advice.a.drop_tag"), { tag: tagLabel(String(v.tag)) });
      case "price_upto": return fill(t("advice.a.price_upto"), { n: fmtNum(v.n as number) });
      case "drop_price": return t("advice.a.drop_price");
      case "drop_floor": return t("advice.a.drop_floor");
      case "drop_group":
        return v.gain
          ? fill(t("advice.a.drop_group"), { group: groupLabel(String(v.group)), gain: fmtNum(v.gain as number) })
          : fill(t("advice.a.drop_group_plain"), { group: groupLabel(String(v.group)) });
      case "drop_groups": {
        const names = (v.groups as string[]).map((g) => `"${groupLabel(g)}"`);
        const list = names.length > 1
          ? `${names.slice(0, -1).join(", ")} ${t("advice.and")} ${names[names.length - 1]}`
          : names[0];
        return fill(t("advice.a.drop_groups"), { groups: list, gain: fmtNum(v.gain as number) });
      }
      case "apply_step": {
        const step = pairLabel(String(v.key), String(v.value));
        if (!step) return null;
        const label = fill(t("advice.a.apply_step"), { step });
        // The count the step leaves, checked server-side before offering it.
        return v.n != null ? `${label} (${fmtNum(v.n as number)})` : label;
      }
    }
    return null;
  }

  function dismiss(it: ListingAdviceItem, track = true) {
    const next = new Set(dismissed);
    next.add(sig(it));
    setDismissed(next);
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
    if (track) trackEvent("listings.advice", { id: it.id, action: "dismiss", vars: it.vars });
  }

  return visible
    .map((it) => ({ it, text: sentence(it) }))
    .filter((r): r is { it: ListingAdviceItem; text: string } => !!r.text)
    .map(({ it, text }) => ({
      key: sig(it),
      text,
      tip: it.id === "people_also",
      actions: it.actions
        .map((a) => ({ a, label: actionLabel(a) }))
        .filter((x): x is { a: ListingAdviceAction; label: string } => !!x.label)
        .map(({ a, label }) => ({
          key: a.id + JSON.stringify(a.vars ?? {}),
          label,
          apply: a.apply,
          onPick: () => {
            trackEvent("listings.advice", { id: it.id, action: a.id, vars: a.vars ?? {} });
            dismiss(it, false);
          },
        })),
    }))
    .filter((r) => r.actions.length > 0);
}
